import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, Clock, ArrowLeft, QrCode, Info, ShieldAlert } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { getPublicBusiness, mediaUrl, createLoyaltyAction, getMyLoyaltyAction } from "@/api";
import { BrandScope } from "@/theme";
import { Loading } from "./Layout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function errText(detail, fallback) {
  if (typeof detail === "string") return detail;
  return fallback;
}

// Phase 4.1: one-tap collect from Google Wallet. This page NEVER mutates loyalty on
// GET — it only calls the EXISTING authenticated POST /api/loyalty-actions after the
// business-scoped customer session is recognized client-side. The Wallet scan_token is
// never used here (it stays staff-side identification only).
export const CollectScreen = () => {
  const { slug } = useParams();
  const { activateBusinessCard, restoreCustomer } = useAuth();
  const navigate = useNavigate();

  const [business, setBusiness] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // phase: resolving | submitting | pending | confirmed | expired | blocked | cooldown | unsupported | unavailable | needAuth | error
  const [phase, setPhase] = useState("resolving");
  const [message, setMessage] = useState("");
  const [action, setAction] = useState(null);
  const [remainingSec, setRemainingSec] = useState(0);
  const [code, setCode] = useState("");
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreErr, setRestoreErr] = useState("");

  const isPoints = business?.loyalty_mode === "points_spend";
  const unit = isPoints ? "Punkte" : "Stempel";

  const submit = useCallback(async () => {
    setPhase("submitting");
    try {
      const a = await createLoyaltyAction();
      if (a.status === "confirmed") { setPhase("confirmed"); return; }
      if (a.status === "expired") { setAction(a); setPhase("expired"); return; }
      setAction(a);
      setPhase("pending");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) { setMessage(errText(detail, "Anfragen sind für diese Treuekarte derzeit deaktiviert. Bitte zeige deine Karte direkt beim Personal vor.")); setPhase("blocked"); }
      else if (status === 429) { setMessage(errText(detail, "Bitte warte einen Moment und versuche es erneut.")); setPhase("cooldown"); }
      else if (status === 400) { setPhase("unsupported"); }
      else { setMessage(errText(detail, "Etwas ist schiefgelaufen. Bitte erneut versuchen.")); setPhase("error"); }
    }
  }, []);

  // Resolve business + recognize existing session; auto-submit when recognized.
  useEffect(() => {
    let alive = true;
    (async () => {
      let b;
      try { b = await getPublicBusiness(slug); }
      catch (e) { if (alive) setNotFound(true); return; }
      if (!alive) return;
      setBusiness(b);
      try { localStorage.setItem("bs_slug", slug); } catch (e) {}
      if (!b.active) { setPhase("unavailable"); return; }
      const u = await activateBusinessCard(b);
      if (!alive) return;
      if (u) submit();
      else setPhase("needAuth");
    })();
    return () => { alive = false; };
  }, [slug, activateBusinessCard, submit]);

  // Pending countdown + status polling (mirrors the existing customer card behavior).
  useEffect(() => {
    if (phase !== "pending" || !action || action.status !== "pending") return;
    let alive = true;
    const tick = () => setRemainingSec(Math.max(0, Math.round((new Date(action.expires_at).getTime() - Date.now()) / 1000)));
    tick();
    const tIv = setInterval(tick, 1000);
    const pIv = setInterval(async () => {
      try {
        const a = await getMyLoyaltyAction(action.id);
        if (!alive) return;
        if (a.status === "confirmed") { setPhase("confirmed"); }
        else if (a.status === "expired") { setPhase("expired"); }
        else setAction((prev) => (prev ? { ...prev, ...a } : a));
      } catch (e) { /* transient */ }
    }, 2500);
    return () => { alive = false; clearInterval(tIv); clearInterval(pIv); };
  }, [phase, action]);

  const doRestore = async (e) => {
    e.preventDefault();
    if (restoreBusy) return;
    setRestoreBusy(true); setRestoreErr("");
    try {
      await restoreCustomer(code.trim());
      try {
        localStorage.setItem("bs_slug", slug);
        localStorage.setItem(`bs_recovery:${slug}`, code.trim());
      } catch (e2) {}
      submit(); // resume the original collect intent — no second manual tap
    } catch (err) {
      if (err?.response?.status === 429) setRestoreErr(errText(err.response?.data?.detail, "Zu viele Fehlversuche. Bitte versuche es in einigen Minuten erneut."));
      else setRestoreErr("Dieser Recovery-Code wurde nicht gefunden. Bitte prüfe deine Eingabe.");
    } finally { setRestoreBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div><p className="font-display font-extrabold text-xl text-slate-900">Betrieb nicht gefunden</p><p className="text-slate-500 text-sm mt-1">Dieser QR-Code ist ungültig.</p></div>
    </div>
  );
  if (!business) return <Loading />;

  const logo = mediaUrl(business.logo_url);
  const Back = () => (
    <button type="button" data-testid="collect-back-button" onClick={() => navigate(`/join/${slug}`)}
      className="mt-6 w-full flex items-center justify-center gap-1 text-sm text-slate-400 font-medium">
      <ArrowLeft className="h-4 w-4" /> Zurück zur Treuekarte
    </button>
  );

  const Frame = ({ children }) => (
    <BrandScope primary={business.primary_color} secondary={business.secondary_color} className="App">
      <div className="max-w-md mx-auto min-h-screen bg-slate-50 sm:border-x border-slate-200 shadow-xl flex flex-col items-center justify-center p-6">
        <div data-testid="collect-screen" className="w-full max-w-sm text-center">
          <div className="mx-auto h-16 w-16 rounded-3xl bg-white ring-1 ring-slate-100 shadow-sm flex items-center justify-center overflow-hidden mb-4">
            {logo ? <img src={logo} alt={business.name} className="h-14 w-14 object-contain" /> : <span className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-primary)" }}>{business.name[0]}</span>}
          </div>
          <p className="text-slate-400 text-[13px] font-semibold mb-1">{business.name}</p>
          {children}
        </div>
      </div>
    </BrandScope>
  );

  if (phase === "resolving" || phase === "submitting") return (
    <Frame>
      <h1 data-testid="collect-submitting" className="font-display font-extrabold text-2xl text-slate-900">Anfrage wird gesendet …</h1>
      <div className="mt-5 flex justify-center"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--brand-primary)" }} /></div>
    </Frame>
  );

  if (phase === "pending") {
    const mm = Math.floor(remainingSec / 60);
    const ss = String(remainingSec % 60).padStart(2, "0");
    return (
      <Frame>
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
        <h1 data-testid="collect-sent" className="font-display font-extrabold text-2xl text-slate-900 mt-3">Anfrage gesendet ✓</h1>
        <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed">
          Deine {unit}-Anfrage wurde an die Kasse gesendet. Bitte warte auf die Bestätigung.
        </p>
        <div data-testid="collect-countdown" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-slate-200 px-4 py-2 text-sm font-bold text-slate-700">
          <Clock className="h-4 w-4" style={{ color: "var(--brand-primary)" }} /> Noch {mm}:{ss} Min. gültig
        </div>
        <Back />
      </Frame>
    );
  }

  if (phase === "confirmed") return (
    <Frame>
      <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
      <h1 data-testid="collect-confirmed" className="font-display font-extrabold text-2xl text-slate-900 mt-3">Gebucht ✓</h1>
      <p className="text-slate-500 text-sm font-medium mt-2">Deine {unit} wurden an der Kasse bestätigt.</p>
      <Back />
    </Frame>
  );

  if (phase === "expired") return (
    <Frame>
      <Info className="mx-auto h-11 w-11 text-slate-400" />
      <h1 data-testid="collect-expired" className="font-display font-extrabold text-2xl text-slate-900 mt-3">Anfrage abgelaufen</h1>
      <p className="text-slate-500 text-sm font-medium mt-2">Die Anfrage ist abgelaufen, bevor sie bestätigt wurde.</p>
      <Button data-testid="collect-retry-button" onClick={submit} className="mt-5 w-full h-14 rounded-2xl font-display font-bold text-base shadow-brand press text-white" style={{ background: "var(--brand-primary)" }}>
        <QrCode className="mr-2 h-5 w-5" /> Erneut senden
      </Button>
      <Back />
    </Frame>
  );

  if (phase === "blocked") return (
    <Frame>
      <ShieldAlert className="mx-auto h-11 w-11 text-slate-400" />
      <h1 data-testid="collect-blocked" className="font-display font-extrabold text-xl text-slate-900 mt-3">Anfrage nicht möglich</h1>
      <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed">{message}</p>
      <Back />
    </Frame>
  );

  if (phase === "cooldown") return (
    <Frame>
      <Clock className="mx-auto h-11 w-11 text-slate-400" />
      <h1 data-testid="collect-cooldown" className="font-display font-extrabold text-xl text-slate-900 mt-3">Kurz warten</h1>
      <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed">{message}</p>
      <Button data-testid="collect-retry-button" onClick={submit} className="mt-5 w-full h-14 rounded-2xl font-display font-bold text-base shadow-brand press text-white" style={{ background: "var(--brand-primary)" }}>
        Erneut versuchen
      </Button>
      <Back />
    </Frame>
  );

  if (phase === "unsupported") return (
    <Frame>
      <Info className="mx-auto h-11 w-11 text-slate-400" />
      <h1 data-testid="collect-unsupported" className="font-display font-extrabold text-xl text-slate-900 mt-3">Bitte an der Kasse vorzeigen</h1>
      <p className="text-slate-500 text-sm font-medium mt-2">Für dieses Programm zeige deine Treuekarte direkt beim Personal vor.</p>
      <Back />
    </Frame>
  );

  if (phase === "unavailable") return (
    <Frame>
      <Info className="mx-auto h-11 w-11 text-slate-400" />
      <h1 data-testid="collect-unavailable" className="font-display font-extrabold text-xl text-slate-900 mt-3">Derzeit nicht verfügbar</h1>
      <p className="text-slate-500 text-sm font-medium mt-2">Das Treueprogramm ist momentan pausiert.</p>
    </Frame>
  );

  if (phase === "needAuth") return (
    <Frame>
      <h1 className="font-display font-extrabold text-2xl text-slate-900">Treuekarte bestätigen</h1>
      <p className="text-slate-400 text-sm font-medium mt-1 mb-4">Gib deinen Recovery-Code ein, um deine Anfrage zu senden.</p>
      <form onSubmit={doRestore} className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-5 space-y-4 text-left">
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-slate-500">Recovery-Code</label>
          <Input data-testid="collect-restore-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Recovery-Code" className="h-12 rounded-xl uppercase tracking-widest font-display font-bold" required autoFocus />
        </div>
        {restoreErr && <p data-testid="collect-restore-error" className="text-sm font-semibold text-rose-600 bg-rose-50 rounded-xl px-3 py-2">{restoreErr}</p>}
        <Button data-testid="collect-restore-submit" type="submit" disabled={restoreBusy} className="w-full h-14 rounded-2xl font-display font-bold text-base shadow-brand press disabled:opacity-60 text-white" style={{ background: "var(--brand-primary)" }}>
          {restoreBusy ? <><Loader2 className="mr-1 h-5 w-5 animate-spin" /> Bitte warten…</> : "Anfrage senden"}
        </Button>
        <button type="button" data-testid="collect-goto-join" onClick={() => navigate(`/join/${slug}?loggedout=1`)} className="w-full text-xs text-slate-400 font-medium underline underline-offset-2">
          Noch keine Karte? Hier erstellen
        </button>
      </form>
    </Frame>
  );

  return (
    <Frame>
      <Info className="mx-auto h-11 w-11 text-slate-400" />
      <h1 data-testid="collect-error" className="font-display font-extrabold text-xl text-slate-900 mt-3">Etwas ist schiefgelaufen</h1>
      <p className="text-slate-500 text-sm font-medium mt-2">{message}</p>
      <Button data-testid="collect-retry-button" onClick={submit} className="mt-5 w-full h-14 rounded-2xl font-display font-bold text-base shadow-brand press text-white" style={{ background: "var(--brand-primary)" }}>
        Erneut versuchen
      </Button>
      <Back />
    </Frame>
  );
};
